// Frontend permission logic — the ONE place that decides who can open which application and where a
// signed-in user lands. App.js routing and tests go through here.
//
// This is NAVIGATION and DIRECT-ROUTE gating only. It is not data authorization: every API still verifies
// the token it receives (movorra-api for the ride-share apps, movorra-flows for Studio). Nothing here makes
// an API accept a token it would otherwise reject.
//
// Rules:
//   • An identity is built from ONE pool's claims. Claims from the Studio pool and the ride-share pool are
//     never merged — the ride-share apps' API only ever sees the ride-share token, so only that token's
//     groups may gate them.
//   • Platform super-admin = the `movorra-admin` group in `cognito:groups`, and nothing else. Legacy `admin`,
//     `operations`, and company membership (`r3_admin`, …) never make someone a super-admin.
//   • Permissions are additive: a super-admin who is also `r3_admin` keeps everything both grant.
//   • `custom:role` is IGNORED (CUSTOM-ROLE-1). The devl ride-share pool allows self sign-up and its web client can
//     write every attribute, so anyone could set their own custom:role. Only groups (admin-assigned) count. Verified
//     on devl 2026-09-13: every account carrying custom:role also holds the matching group, so nobody loses access.
import { catalog as defaultCatalog } from '../applications/applicationCatalog';

export const SUPER_ADMIN_GROUP = 'movorra-admin';
export const STUDIO_PATH = '/studio';

const lower = (v) => String(v || '').trim().toLowerCase();

/** A signed-in identity from ONE pool's id-token claims. */
export function identityFromClaims(claims, pool = 'rideshare') {
  const c = claims || {};
  const raw = Array.isArray(c['cognito:groups']) ? c['cognito:groups'] : [];
  const groups = new Set(raw.map(lower).filter(Boolean));
  return Object.freeze({
    pool,
    groups,              // cognito:groups — admin-assigned, verified by the APIs
    roleGroups: groups,  // what company application requirements are checked against (groups only)
    isSuperAdmin: groups.has(SUPER_ADMIN_GROUP),
  });
}

/** Company-role groups as a lower-cased Set (cognito:groups only; custom:role is ignored). */
export function getGroups(claims) {
  return new Set(identityFromClaims(claims).roleGroups);
}

/** True when a group Set satisfies a requirement list (empty list = open). */
export function canAccess(requiredGroups, groups) {
  if (!requiredGroups || requiredGroups.length === 0) return true;
  return requiredGroups.some((g) => groups.has(g));
}

/** May this identity open a route config from routing/resolveProduct? */
export function canOpenRoute(identity, cfg) {
  if (!cfg) return false;
  if (canAccess(cfg.requiredGroups, identity.roleGroups)) return true;
  // Additive super-admin grant: every COMPANY application route, for an identity from the pool that app's API
  // trusts. Platform routes (demo, landing) carry no appId and are governed by their own requirements.
  return identity.isSuperAdmin && !!cfg.appId && (cfg.identityPool || 'rideshare') === identity.pool;
}

/** May this identity open an application definition? Public apps are open to everyone. */
export function canOpenApplication(identity, app) {
  if (!app) return false;
  if (app.access && app.access.public) return true;
  if (identity.isSuperAdmin && (app.access.identityPool || 'rideshare') === identity.pool) return true;
  return canAccess(app.access.anyOfGroups, identity.roleGroups);
}

/**
 * The company home for a set of company-role groups (no super-admin handling). Peoria first so a mixed
 * identity is never pulled into R3.
 */
export function homePathForGroups(groups) {
  if (groups.has('peoria_operations') || groups.has('peoria_admin')) return '/peoriaops';
  if (groups.has('peoria_customer')) return '/peoriachat';
  // Krapf, like Peoria, ahead of R3 so a mixed identity is never pulled into R3. Krapf has exactly one
  // application (Charter Mail), so its staff land straight on it — without this they fell through to the
  // final return and were sent to the R3 customer chat, an app they cannot use.
  if (groups.has('krapf_operations') || groups.has('krapf_admin')) return '/krapfmail';
  if (groups.has('r3_admin') || groups.has('admin')) return '/r3ops';
  if (groups.has('r3_dispatcher') || groups.has('operations') || groups.has('ops')) return '/r3dispatch';
  if (groups.has('r3_driver') || groups.has('driver') || groups.has('drivers')) return '/r3driver';
  return '/r3chat';
}

/**
 * COMPANY-STUDIO-1: the company Studio a company-login identity may use (e.g. an R3 administrator → R3, screens
 * Applications + Integration), or null. Super-admins are handled separately (full Studio).
 */
export function companyStudioAccess(identity, cat = defaultCatalog) {
  if (!identity || identity.pool !== 'rideshare') return null;
  return cat.companyStudioForGroups(identity.roleGroups);
}

/**
 * Where a signed-in identity lands: Studio for a Movorra super-admin or a company administrator with Studio access
 * (/studio is the one sign-in link); everyone else on their company home.
 */
export function landingPath(identity, cat = defaultCatalog) {
  if (identity.isSuperAdmin) return STUDIO_PATH;
  if (companyStudioAccess(identity, cat)) return STUDIO_PATH;
  return homePathForGroups(identity.roleGroups);
}
